import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import WebService from "../Services/WebService";
import { mediaUrl } from "../Services/mediaUrl";
import styles from "./RawMaterialDetailPage.module.css";
import {
  FaEllipsisV, FaBoxOpen, FaTools, FaTruck, FaShippingFast,
  FaClipboardList, FaCheckCircle, FaTag
} from "react-icons/fa";
import { toast } from "react-toastify";
import AdjustStockModal from "./AdjustStockModal";
import StockHistory from "./StockHistory";
import { InventoryItemType, StockHistoryView } from "./inventoryHistory";
import { useBladeBack, useInBlade } from "../Customs/FullWidthBlade/FullWidthBlade";

// --- Helper to handle N/A display everywhere ---
const displayValue = (val: any, opts?: { currency?: boolean, unit?: string }) => {
  if (
    val === null ||
    val === undefined ||
    val === "" ||
    (typeof val === "number" && Number.isNaN(val))
  ) return "N/A";
  if (opts?.currency) return `₹${val}`;
  if (opts?.unit) return `${val}${opts.unit}`;
  return val;
};

// Chip indicator for boolean values
const BoolChip: React.FC<{ value?: boolean }> = ({ value }) => {
  if (value === undefined || value === null) return <span className={styles.boolChip + " " + styles.na}>N/A</span>;
  return value
    ? <span className={styles.boolChip + " " + styles.chipYes}>Yes</span>
    : <span className={styles.boolChip + " " + styles.chipNo}>No</span>;
};

const RawMaterialDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const handleBack = useBladeBack("/raw-material");
  const inBlade = useInBlade();
  const pageStyle = inBlade ? { marginTop: 0, minHeight: "100%" } : undefined;

  const [loading, setLoading] = useState(true);
  const [rawMaterial, setRawMaterial] = useState<any>(null);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<"details" | "transactions" | "inventory">("details");
  const [menuOpen, setMenuOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showAdjustStock, setShowAdjustStock] = useState(false);

  // Fetch details
  const fetchDetails = () => {
    if (!id) return;
    setLoading(true);
    setError("");
    WebService.getAPI<{ data: any }>({
      action: `/v1/door-management/raw-material/${id}`,
      isShowError: false,
    })
      .then((res) => {
        const found = res?.data;
        if (!found || found.id == null) {
          setError("Material not found.");
          setRawMaterial(null);
        } else {
          setRawMaterial({
            ...found,
            stock_on_hand: found.quantity ?? null,
          });
          setIsActive(found.status === "ACTIVE");
        }
      })
      .catch((err) => {
        const msg =
          (typeof err === "string" && err) ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load raw material details";
        setError(msg);
        setRawMaterial(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      const menu = document.getElementById("rmdetail-dot-menu");
      if (menu && !menu.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  // -- ACTIVE/INACTIVE toggle handler --
  const handleToggleStatus = async () => {
    if (!rawMaterial) return;
    setUpdatingStatus(true);
    const newStatus = isActive ? "INACTIVE" : "ACTIVE";
    const payload = {
      ...rawMaterial,
      status: newStatus,
    };
    // Remove any local-only fields before sending update
    delete payload.stock_on_hand;

    try {
      const res = await WebService.putAPI({
        action: `/v1/door-management/raw-material`,
        body: payload,
      }) as { status_code?: number; message?: string };

      if (res?.status_code === 200) {
        setIsActive(!isActive);
        fetchDetails();
        toast.success("Status updated successfully!");
      } else {
        toast.error(res?.message || "Failed to update status");
      }
    } catch (e) {
      // toast.error("Failed to update status");
    }
    setUpdatingStatus(false);
  };

  if (loading)
    return (
      <div className={styles.pageContainer} style={pageStyle}>
        <button className={styles.backBtn} onClick={handleBack}>
          {"←"} Back to Inventory
        </button>
        <div style={{ padding: 50, fontSize: 18, textAlign: "center" }}>
          Loading...
        </div>
      </div>
    );
  if (error)
    return (
      <div className={styles.pageContainer} style={pageStyle}>
        <button className={styles.backBtn} onClick={handleBack}>
          {"←"} Back to Inventory
        </button>
        <div style={{ padding: 50, color: "red", textAlign: "center" }}>
          {error}
        </div>
      </div>
    );
  if (!rawMaterial)
    return (
      <div className={styles.pageContainer} style={pageStyle}>
        <button className={styles.backBtn} onClick={handleBack}>
          {"←"} Back to Inventory
        </button>
        <div style={{ padding: 50, textAlign: "center" }}>No data found.</div>
      </div>
    );

  return (
    <div className={styles.pageContainer} style={pageStyle}>
      {/* --- Back to Inventory Button --- */}
      <button className={styles.backBtn} onClick={handleBack}>
        {"←"} Back to Inventory
      </button>

      {/* Tabs + 3-dot menu */}
<div className={styles.headerRow}>
  <div className={styles.tabsRow}>
    <div
      className={`${styles.tab} ${activeTab === "details" ? styles.active : ""}`}
      onClick={() => {
        setActiveTab("details");
        setMenuOpen(false);
      }}
    >
      Details
    </div>
    <div
      className={`${styles.tab} ${activeTab === "transactions" ? styles.active : ""}`}
      onClick={() => {
        setActiveTab("transactions");
        setMenuOpen(false);
      }}
    >
      Transaction History
    </div>
    <div
      className={`${styles.tab} ${activeTab === "inventory" ? styles.active : ""}`}
      onClick={() => {
        setActiveTab("inventory");
        setMenuOpen(false);
      }}
    >
      Inventory History
    </div>
  </div>
  {activeTab === "details" && (
    <div style={{ position: "relative" }}>
      <button className={styles.menuButton} onClick={() => setMenuOpen((v) => !v)}>
        <FaEllipsisV size={20} />
      </button>
      {menuOpen && (
        <div id="rmdetail-dot-menu" className={styles.menuDropdown}>
          <div
            className={styles.menuOption}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            Mark as Active
            <label
              className={styles.menuToggleSwitch}
              style={{
                marginLeft: 12,
                cursor: updatingStatus ? "not-allowed" : "pointer",
                opacity: updatingStatus ? 0.5 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={isActive}
                onChange={handleToggleStatus}
                disabled={updatingStatus}
              />
              <span className={styles.menuToggleSlider}></span>
            </label>
          </div>
          <div
            className={styles.menuOption}
            onClick={() => {
              setShowAdjustStock(true);
              setMenuOpen(false);
            }}
          >
            Adjust stock
          </div>
        </div>
      )}
    </div>
  )}
</div>

      {activeTab === "details" ? (
        <>
          <div className={styles.bigGrid}>
        {/* Main details card */}
        <div className={styles.detailsCard}>
          <div className={styles.detailTopRow}>
            <div className={styles.imageHolder}>
              <img
                src={
                  rawMaterial.images?.[0]?.file_name
                    ? mediaUrl(rawMaterial.images[0].file_name)
                    : "https://placehold.co/148x120?text=No+Image"
                }
                alt={displayValue(rawMaterial.name)}
              />
            </div>
            <div className={styles.metaGrid}>
              <div>
                <span className={styles.metaLabel}>Item Code</span>
                <span className={styles.metaValue}>{displayValue(rawMaterial.sku_id)}</span>
              </div>
              <div>
                <span className={styles.metaLabel}>Item Name</span>
                <span className={styles.metaValue}>{displayValue(rawMaterial.name)}</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Description</span>
                <span className={styles.metaValueDesc}>{displayValue(rawMaterial.description)}</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Category</span>
                <span className={styles.categoryTag}>
                  <FaTag style={{ marginRight: 4, marginBottom: -2, color: "#26b365" }} />
                  {displayValue(rawMaterial.category)}
                </span>
              </div>
              {/* Add Image Button below Category */}
              <div style={{ marginTop: 13 }}>
                <label
                  htmlFor="rawMaterialImageUpload"
                  style={{
                    background: "#FFD400",
                    color: "#232323",
                    fontWeight: 600,
                    fontSize: 15,
                    padding: "10px 32px",
                    borderRadius: 4,
                    cursor: "pointer",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    border: "1.2px solid #ffd400",
                    display: "inline-block",
                    transition: "background 0.12s"
                  }}
                >
                  + Add Image
                  <input
                    id="rawMaterialImageUpload"
                    type="file"
                    accept="image/png,image/jpeg"
                    style={{ display: "none" }}
                    onChange={async (e) => {
                      if (!e.target.files || !e.target.files[0]) return;
                      const file = e.target.files[0];
                      if (!["image/jpeg", "image/png"].includes(file.type)) {
                        toast.error("Only PNG or JPG images are allowed.");
                        return;
                      }
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error("Image must be under 5MB.");
                        return;
                      }
                      try {
                        await WebService.fileUploadAPI({
                          action: `/v1/door-management/raw-material/images/${rawMaterial.id}`,
                          file,
                          key: "file",
                          body: {},
                        });
                        toast.success("Image added successfully!");
                        fetchDetails();
                      } catch (err) {
                        toast.error("Failed to upload image.");
                      }
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
          <div className={styles.priceToggleRow}>
            <div className={styles.priceGroup}>
              <div>
                <span className={styles.priceLabel}>Selling Price</span>
                <span className={styles.priceValue}>
                  {displayValue(rawMaterial.selling_price, { currency: true })}
                </span>
                <span className={styles.unitText}>
                  per {displayValue(rawMaterial.measuring_unit)}
                </span>
              </div>
              <div>
                <span className={styles.priceLabel}>Purchase Cost</span>
                <span className={styles.priceValue}>
                  {displayValue(rawMaterial.cost_price, { currency: true })}
                </span>
                <span className={styles.unitText}>
                  per {displayValue(rawMaterial.measuring_unit)}
                </span>
              </div>
            </div>
            <div className={styles.togglesGroup}>
              <div>
                <span className={styles.toggleTitle}>Available for Sales</span>
                <BoolChip value={!!rawMaterial.is_trading_allowed} />
                <span className={styles.toggleTitle} style={{marginLeft:12}}>Available for Return</span>
                <BoolChip value={!!rawMaterial.is_return_allowed} />
              </div>
            </div>
          </div>
        </div>
        {/* Stock Summary card */}
        <div className={styles.stockSummaryCard}>
          <div className={styles.cardTitle}>Stock Summary</div>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryBox + " " + styles.stockHand}>
              <span className={styles.sumLabel}>
                <FaClipboardList style={{ marginRight: 6 }} />
                Stock on Hand
              </span>
              <span className={styles.sumValue}>{displayValue(rawMaterial.stock_on_hand)}</span>
            </div>
            <div className={styles.summaryBox + " " + styles.available}>
              <span className={styles.sumLabel}>
                <FaCheckCircle style={{ marginRight: 6 }} />
                Available
              </span>
              <span className={styles.sumValue}>N/A</span>
            </div>
            <div className={styles.summaryBox + " " + styles.inProduction}>
              <span className={styles.sumLabel}>
                <FaTools style={{ marginRight: 6 }} />
                In Production
              </span>
              <span className={styles.sumValue}>N/A</span>
            </div>
          </div>
        </div>
        {/* Measuring Unit (yellow) card */}
        <div className={styles.measuringUnitCard}>
          <div className={styles.yellowHeaderRow}>
            <FaBoxOpen style={{ marginRight: 7, color: "#e1bb00" }} />
            <span className={styles.yellowTitle}>measuring unit</span>
            <span className={styles.yellowUnit}>{displayValue(rawMaterial.measuring_unit)}</span>
          </div>
          <div className={styles.yellowGrid}>
            <div className={styles.yellowLabelCol}>
              <div>Measuring unit</div>
              <div>Distribution selling price</div>
              <div>GST Percentage</div>
            </div>
            <div className={styles.yellowValueCol}>
              <div>{displayValue(rawMaterial.measuring_unit_value)}</div>
              <div>{displayValue(rawMaterial.distributor_selling_price, { currency: true })}</div>
              <div>{displayValue(rawMaterial.gst_percentage, { unit: "%" })}</div>
            </div>
          </div>
        </div>
        {/* Stock Status card */}
        <div className={styles.stockStatusCard}>
          <div className={styles.cardTitle}>Stock Status</div>
          <div className={styles.statusGrid}>
            <div className={styles.statusBox + " " + styles.statusPurple}>
              <span className={styles.statusLabel}>
                <FaShippingFast style={{ marginRight: 7 }} />
                To be Shipped
              </span>
              <span className={styles.statusValue}>N/A</span>
            </div>
            <div className={styles.statusBox + " " + styles.statusBlue}>
              <span className={styles.statusLabel}>
                <FaTruck style={{ marginRight: 7 }} />
                Out for delivery
              </span>
              <span className={styles.statusValue}>N/A</span>
            </div>
            <div className={styles.statusBox + " " + styles.statusLightBlue}>
              <span className={styles.statusLabel}>
                <FaBoxOpen style={{ marginRight: 7 }} />
                To be Received
              </span>
              <span className={styles.statusValue}>N/A</span>
            </div>
          </div>
        </div>
        </div>

        {/* Adjust Stock Modal */}
        {showAdjustStock && rawMaterial && (
          <AdjustStockModal
            open={showAdjustStock}
            onClose={() => setShowAdjustStock(false)}
            rawMaterial={{
              id: rawMaterial.id,
              name: rawMaterial.name,
              sku_id: rawMaterial.sku_id,
              images: rawMaterial.images,
              quantity: rawMaterial.quantity,
            }}
            mode="raw-material"
            onSuccess={() => {
              setShowAdjustStock(false);
              fetchDetails();
            }}
          />
        )}
        </>
      ) : (
        <StockHistory
          itemType={InventoryItemType.RAW_MATERIAL}
          itemId={rawMaterial.id}
          view={activeTab === "inventory" ? StockHistoryView.INVENTORY : StockHistoryView.TRANSACTIONS}
        />
      )}
    </div>
  );
};

export default RawMaterialDetailPage;
